import { useEffect, useRef } from "react";

/**
 * Ambient animated stage used on the BiorVault landing.
 *  - Pure canvas particle field (no 3D library)
 *  - CSS orbital rings are rendered as siblings via <AmbientOrbits /> if desired
 *  - Respects prefers-reduced-motion
 */
export function AmbientCanvas({ density = 70, tint = "123, 124, 255" }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const reducedRef = useRef(false);
  const visibleRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");

    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedRef.current = mq.matches;
    const onMq = () => { reducedRef.current = mq.matches; };
    mq.addEventListener?.("change", onMq);

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // Pause the animation loop when the hero is scrolled off-screen so we
    // don't burn GPU/CPU redrawing particles nobody's looking at.
    const io = typeof window.IntersectionObserver === "function"
      ? new IntersectionObserver((entries) => {
          visibleRef.current = entries.some((e) => e.isIntersecting);
        }, { rootMargin: "200px" })
      : null;
    io?.observe(canvas);

    // Also pause when the tab is backgrounded.
    const onVisibility = () => {
      if (document.visibilityState === "hidden") visibleRef.current = false;
      else visibleRef.current = true;
    };
    document.addEventListener("visibilitychange", onVisibility);

    const count = density;
    const particles = Array.from({ length: count }, () => ({
      x: Math.random(),
      y: Math.random(),
      z: Math.random(),
      r: Math.random() * 1.2 + 0.3,
      vx: (Math.random() - 0.5) * 0.00025,
      vy: (Math.random() - 0.5) * 0.00025,
      phase: Math.random() * Math.PI * 2,
    }));

    let t0 = performance.now();

    const draw = (now) => {
      if (!visibleRef.current) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      const elapsed = (now - t0) / 1000;
      ctx.clearRect(0, 0, width, height);

      // subtle aurora tint underlay
      const grad = ctx.createRadialGradient(
        width * 0.18, height * 0.18, 0,
        width * 0.18, height * 0.18, Math.max(width, height) * 0.6
      );
      grad.addColorStop(0, `rgba(${tint}, 0.12)`);
      grad.addColorStop(0.6, `rgba(${tint}, 0.02)`);
      grad.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      for (const p of particles) {
        if (!reducedRef.current) {
          p.x += p.vx;
          p.y += p.vy;
        }
        if (p.x < 0) p.x += 1; else if (p.x > 1) p.x -= 1;
        if (p.y < 0) p.y += 1; else if (p.y > 1) p.y -= 1;

        const twinkle = 0.45 + 0.55 * Math.abs(Math.sin(elapsed * 1.2 + p.phase));
        const alpha = (0.18 + p.z * 0.55) * twinkle;
        const size = p.r * (0.6 + p.z);

        ctx.beginPath();
        ctx.fillStyle = `rgba(${tint}, ${alpha})`;
        ctx.arc(p.x * width, p.y * height, size, 0, Math.PI * 2);
        ctx.fill();

        // soft halo on the brightest particles
        if (p.z > 0.72) {
          ctx.beginPath();
          ctx.fillStyle = `rgba(${tint}, ${alpha * 0.14})`;
          ctx.arc(p.x * width, p.y * height, size * 4.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      io?.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      mq.removeEventListener?.("change", onMq);
    };
  }, [density, tint]);

  return <canvas ref={canvasRef} className="ambient-canvas" aria-hidden="true" />;
}

/** Two soft dashed orbital rings that rotate slowly in opposite directions. */
export function AmbientOrbits() {
  return (
    <div className="ambient-orbits" aria-hidden="true">
      <span className="ambient-orbits__ring ambient-orbits__ring--outer">
        <span className="ambient-orbits__dot" style={{ "--a": "0deg" }} />
        <span className="ambient-orbits__dot" style={{ "--a": "90deg" }} />
        <span className="ambient-orbits__dot" style={{ "--a": "210deg" }} />
      </span>
      <span className="ambient-orbits__ring ambient-orbits__ring--inner">
        <span className="ambient-orbits__dot ambient-orbits__dot--small" style={{ "--a": "30deg" }} />
        <span className="ambient-orbits__dot ambient-orbits__dot--small" style={{ "--a": "160deg" }} />
        <span className="ambient-orbits__dot ambient-orbits__dot--small" style={{ "--a": "290deg" }} />
      </span>
    </div>
  );
}

export default AmbientCanvas;
